import React, { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/comparison-quote-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="comparison-cta" className="py-20 bg-gradient-to-br from-teal-600 via-teal-700 to-blue-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            Ready to Save $123,000+ Over 1.5 Years?
          </h2>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto">
            Get a free consultation and detailed cost analysis for your next high-rise construction project.{' '}
            See exactly how Dinari Designs can reduce your costs while improving quality and eliminating risk.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="bg-white rounded-xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Request Your Free Cost Analysis</h3>

            <form id="comparison-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(604) 555-0123"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Details (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-bold py-4 px-6 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                {isSubmitting ? 'Sending...' : 'Get Free Cost Analysis'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-600 mr-2" />
                    <span className="text-green-800">
                      Thank you! We'll contact you within 24 hours with your personalized cost analysis.
                    </span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 mr-2" />
                    <span className="text-red-800">
                      Sorry, there was an error. Please call us directly at (604) 240-2889.
                    </span>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className="space-y-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">What You'll Receive:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-300 mr-3 mt-1 text-xl flex-shrink-0" />
                  <span>Detailed cost comparison specific to your project</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-300 mr-3 mt-1 text-xl flex-shrink-0" />
                  <span>Risk assessment and mitigation strategies</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-300 mr-3 mt-1 text-xl flex-shrink-0" />
                  <span>Timeline and deliverables breakdown</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-300 mr-3 mt-1 text-xl flex-shrink-0" />
                  <span>Sample detailing packages from similar projects</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-300 mr-3 mt-1 text-xl flex-shrink-0" />
                  <span>Free 30‑minute consultation with Matthew Beltrame</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Prefer to Talk?</h3>
              <p className="text-gray-700 mb-6">
                Call Matthew directly for immediate consultation and cost analysis.
              </p>
              <a
                href="tel:+1-604-240-2889"
                className="flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill mr-3" />
                Call: (604) 240-2889
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white text-center">
              <p className="text-sm text-teal-100">
                Join 50+ successful projects across Vancouver, Burnaby, Surrey, and Victoria
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
